// react
import { useEffect, useMemo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { createPortal } from 'react-dom';

// store
import { useSelector, dispatch } from 'store';
import { getAccount, getIsSigningOut } from 'store/slices/iamSlice';
import { newToast } from 'store/slices/uiSlice';
import { token } from 'store/slices/token';
import { useSignInKindeMutation } from 'store/api/iam';
import {
  useGetSessionCareCircleMemberQuery,
  useGetSessionCareCircleQuery,
  useLazyGetByAccountQuery,
} from 'store/api/careCircle';
import { useCreateTestNotificationMutation } from 'store/api/notification';

// routes
import { Router } from 'router';

// components
import { AppUpdateDialog } from '@karehero/llama/components/organisms/AppUpdateDialog/AppUpdateDialog';
import { DevTools } from '@karehero/llama/components/atoms/Version/DevTools';

import OfflineOverlay from '@karehero/llama/components/pages/OfflineOverlay/OfflineOverlay';
import { ThemeProvider } from '@karehero/llama/ThemeProvider';

// hooks
import {
  useAppUpdate,
  useAuth,
  useTracking,
  useOnlineStatus,
  useWindowSize,
  useIntercom,
  useApp,
  track,
  TrackingEvent,
} from 'hooks';

// posthog
import { identifyUser } from 'postHog';

// capacitor
import { Capacitor } from '@capacitor/core';

// context
import { TenantProvider, AccountRole } from 'context';
import { DirectoryInteractionsProvider } from '@karehero/llama/contexts/DirectoryInteractions';

import * as Sentry from '@sentry/react';
import Hotjar from '@hotjar/browser';

/**
 * App is the main component of the application.
 */
const App = () => {
  // hooks
  const windowSize = useWindowSize();
  const account = useSelector(getAccount);
  const isSigningOut = useSelector(getIsSigningOut);

  const isOnline = useOnlineStatus();
  const [signInKinde] = useSignInKindeMutation();

  const { getToken, isAuthenticated, isLoading, signOut } = useAuth();
  token.setTokenGetter(async () => {
    if (!getToken) return '';
    return await getToken();
  });

  const [getByAccount] = useLazyGetByAccountQuery();

  const [createTestNotification] = useCreateTestNotificationMutation();
  const { isApp, isApple, isAndroid } = useApp();
  const { isDialogVisible, setIsDialogVisible, updateApp } = useAppUpdate();
  useTracking();
  useGetSessionCareCircleQuery(undefined, {
    skip:
      !account ||
      !isAuthenticated ||
      isLoading ||
      account.accountRole.name !== 'Member',
  });
  useGetSessionCareCircleMemberQuery(undefined, {
    skip:
      !account ||
      !isAuthenticated ||
      isLoading ||
      account.accountRole.name !== 'Member',
  });

  // effects
  useEffect(() => {
    const href = window?.location?.href;

    if (!href) return;

    const url = new URL(href);
    const hostname = url.host;

    if (!hostname) return;

    // If it has a trailing dot then replace this with hostname without the dot
    if (hostname.charAt(hostname.length - 1) === '.') {
      url.host = hostname.substring(0, hostname.length - 1);

      window.location.href = url.toString();
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || isLoading || account || isSigningOut) return;

    signInKinde({}).catch((err) => {
      console.error(`Error signing in`, err);
    });
  }, [account, isAuthenticated, isLoading, isSigningOut, signInKinde]);

  useEffect(() => {
    (async () => {
      if (!isAuthenticated || isLoading || !account) return;

      if (isApp && isApple) {
        track(TrackingEvent.AuthenticatedIOS, {});
      } else if (isApp && isAndroid) {
        track(TrackingEvent.AuthenticatedAndroid, {});
      } else {
        track(TrackingEvent.AuthenticatedBrowser, {});
      }

      Sentry.setUser({
        email: account.email,
        id: account.id,
        name: `${account.firstName} ${account.lastName}`,
      });

      Hotjar.identify(account.id, {
        email: account.email,
        name: `${account.firstName} ${account.lastName}`,

        organization: account?.organization?.name,
        organizationId: account?.organization?.id,
        organizationType: account?.organization?.organizationTypeId,

        isApp,
        isApple,
        isAndroid,

        accountRole: account?.accountRole?.name,
        isVerified: account?.isVerified,
        isOnboarded: account?.isOnboarded,

        nodeEnvironment: import.meta.env.NODE_ENV,
        buildEnvironment: `${import.meta.env.REACT_APP_BUILD_ENVIRONMENT}`,
        platform: Capacitor.getPlatform(),
        buildVersion: import.meta.env.REACT_APP_BUILD_VERSION,
        buildHash: import.meta.env.REACT_APP_BUILD_HASH,
      });
    })();
  }, [isAuthenticated, isLoading, account, isApp, isApple, isAndroid]);

  useEffect(() => {
    if (
      isAuthenticated &&
      !isLoading &&
      account &&
      account.accountRole.name === AccountRole.MEMBER
    ) {
      getByAccount();
    }
  }, [getByAccount, account, isLoading, isAuthenticated]);

  useEffect(() => {
    if (account) {
      identifyUser(account);
    }
  }, [account]);

  // ENG-3071: Log auth state when the app resumes from background. This helps
  // diagnose whether logouts are caused by Kinde losing state, token refresh
  // failures, or something else entirely.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const hasAccount = !!account;
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'app resumed from background',
        level: 'info',
        data: {
          isAuthenticated,
          isLoading,
          hasAccount,
          platform: Capacitor.getPlatform(),
        },
      });

      // If Kinde says not authenticated but we have a cached account,
      // the user is about to be effectively logged out
      if (!isAuthenticated && hasAccount && !isLoading) {
        Sentry.captureMessage('auth.session_lost_on_resume', {
          level: 'warning',
          tags: {
            'auth.trigger': 'visibility_change',
            'auth.platform': Capacitor.getPlatform(),
          },
          extra: {
            isAuthenticated,
            isLoading,
            hasAccount,
          },
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, isLoading, account]);

  // memo
  const isAccountLoading = useMemo(() => {
    if (isLoading) return true;

    if (!account && isAuthenticated) return true;

    return false;
  }, [isLoading, account, isAuthenticated]);

  // global styles
  const Global = createGlobalStyle`
    #hubspot-messages-iframe-container {
      opacity: 0;
      pointer-events: none;
      touch-action: none;
    }
  `;

  return (
    <ThemeProvider>
      <TenantProvider>
        <Global />
        <AppWrapper height={windowSize[1]}>
          {isApp && <SafeAreaBottom />}
          <DirectoryInteractionsProvider isShowResultsInApp={true}>
            <DevTools
              nodeEnvironment={import.meta.env.NODE_ENV}
              buildEnvironment={`${import.meta.env.REACT_APP_BUILD_ENVIRONMENT}`}
              platform={Capacitor.getPlatform()}
              buildVersion={import.meta.env.REACT_APP_BUILD_VERSION}
              buildHash={import.meta.env.REACT_APP_BUILD_HASH}
              onSignOut={signOut}
              onTestErrorToast={() =>
                dispatch(
                  newToast({
                    title: 'Test Error',
                    description:
                      'Say toast. Say toast toast toast. Spell toast. What do you put in a toaster?',
                    variant: 'error',
                  }),
                )
              }
              onTestNotification={(params) => createTestNotification(params)}
            />
            {!isOnline &&
              createPortal(
                <OfflineOverlay />,
                document.getElementById('offline-overlay')!,
              )}
            <RouterWrapper>
              <AppUpdateDialog
                isDialogVisible={isDialogVisible}
                setIsDialogVisible={setIsDialogVisible}
                onUpdate={updateApp}
              />
              {!isOnline &&
                createPortal(
                  <OfflineOverlay />,
                  document.getElementById('offline-overlay')!,
                )}
              {!isAccountLoading && <Router />}
            </RouterWrapper>
          </DirectoryInteractionsProvider>
        </AppWrapper>
      </TenantProvider>
    </ThemeProvider>
  );
};

interface AppWrapperProps {
  height: number;
}
const AppWrapper = styled.div<AppWrapperProps>`
  display: flex;
  flex-direction: column;
  height: ${(props) => props.height}px;
  position: relative;
  z-index: 0;
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
`;

const SafeAreaBottom = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: env(safe-area-inset-bottom);
  background-color: #edf3da;
  z-index: 1000;
`;

const RouterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  position: relative;
`;

export default App;
